import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { ActivityInterface } from 'src/app/models/activity.interface';
import { PicLinkInterface } from 'src/app/models/pic-link.interface';
import { ActivityFilterComponent } from '../../core/activity-filter/activity-filter.component';
import { LocationContextService } from 'src/app/services/location-context.service';
import { ServiceService } from 'src/app/services/service.service';
import { StorageService } from 'src/app/services/storage.service';
import { SiteConfigurationMetadata } from 'src/app/models/site-configuration-metadata.interface';
import type { Schema } from '../../../../../amplify/data/resource';

type SiteConfigurationSchema = Schema['SiteConfiguration']['type'];

@Component({
    selector: 'app-home',
    templateUrl: './home.component.html',
    styleUrls: ['./home.component.scss'],
    standalone: true,
    imports: [CommonModule, MatIconModule, ActivityFilterComponent]
})
export class HomeComponent implements OnInit {
  adventureTypes: PicLinkInterface[] = [];
  activities!: ActivityInterface;
  searchText!: string;
  heroHeadline = 'Find Your Adventure!';
  /** Hero still image from site config only (no local asset fallback). */
  heroBannerUrl = '';
  heroVideoUrl: string | null = null;

  /** Site description from config (shown under headline). */
  siteDescription: string | null = null;
  /** Brand colors as CSS variables on `.home-root`. */
  sitePrimaryColor: string | null = null;
  siteSecondaryColor: string | null = null;
  hasPlayed = false;

  /** Default tile labels when `metadata.categoryOrder` is missing (legacy). */
  readonly defaultCategoryOrder: string[] = [
    'Snorkeling',
    'Suba Diving',
    'Boat Tours',
    'Bus Tours',
    'Beach Tours',
    "4x4 & ATV's",
    'Wild Life',
    'Luxery & Private Tours',
    'Hotel',
    'Car Rental'
  ];

  /** Per-location category names for the 10 tiles (from site config metadata). */
  displayCategoryNames: string[] = [...this.defaultCategoryOrder];

  /** URLs from site config `metadata.categoryImages` only (no local asset fallback). */
  private categoryImageUrls: Record<string, string> = {};

  constructor(
    private locationContext: LocationContextService,
    private service: ServiceService,
    private storageService: StorageService
  ) {}

  /** CSS custom properties for brand colors (bound to `.home-root`). */
  get homeStyles(): Record<string, string> {
    const s: Record<string, string> = {};
    if (this.sitePrimaryColor) {
      s['--site-primary'] = this.sitePrimaryColor;
    }
    if (this.siteSecondaryColor) {
      s['--site-secondary'] = this.siteSecondaryColor;
    }
    return s;
  }

  /** Category names from `displayCategoryOrder` that have a non-empty image URL (tiles with no image are omitted). */
  get categoriesWithImages(): string[] {
    return this.displayCategoryNames.filter((n) => this.getCategoryImageUrl(n).trim().length > 0);
  }

  /**
   * Large-screen mosaic: same structure as the original static layout — left column (large, mid+mid, large, small+small),
   * then right column (stacked mids). Categories are taken in `categoriesWithImages` order.
   */
  get categoryMosaic(): {
    leftLarge1: string | null;
    leftPairMid: string[];
    leftLarge2: string | null;
    leftPairSmall: string[];
    rightMids: string[];
  } {
    const c = this.categoriesWithImages;
    return {
      leftLarge1: c[0] ?? null,
      leftPairMid: c.slice(1, 3),
      leftLarge2: c[3] ?? null,
      leftPairSmall: c.slice(4, 6),
      rightMids: c.slice(6)
    };
  }

  ngOnInit() {
    this.resetHomeDefaults();

    // Show all activities by default (empty string means no filter)
    this.searchText = '';

    this.loadHomeSiteConfiguration();
  }

  handleFirstPlay(event: Event) {
    if (!this.hasPlayed) {
      this.hasPlayed = true;

      const vid = event.target;
      if (!(vid instanceof HTMLVideoElement)) {
        return;
      }

      // Remove listener so this only gets called once.
      vid.removeEventListener('play', this.handleFirstPlay);

      // Start whatever you need to do after first playback has started
    }
  }

  /** Chromium often ignores autoplay when the video src is bound after async config load. */
  onHeroVideoReady(video: EventTarget | null): void {
    if (!(video instanceof HTMLVideoElement)) {
      return;
    }
    video.muted = true;
    void video.play().catch(() => {
      // Browser blocked autoplay; poster / still image remains visible.
    });
  }

  /** Fall back to the configured hero image when the video cannot be decoded or fetched. */
  onHeroVideoError(): void {
    this.heroVideoUrl = null;
  }

  getSearchText(search: string) {
    this.searchText = search;
    // Scroll to the activity filter section
    setTimeout(() => {
      const filterElement = document.querySelector('.filterArea');
      if (filterElement) {
        filterElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  }

  getCategoryImageUrl(categoryName: string): string {
    return this.categoryImageUrls[categoryName]?.trim() ?? '';
  }

  private resetHomeDefaults(): void {
    this.heroVideoUrl = null;
    this.heroBannerUrl = '';
    this.heroHeadline = 'Find Your Adventure!';
    this.siteDescription = null;
    this.sitePrimaryColor = null;
    this.siteSecondaryColor = null;
    this.categoryImageUrls = {};
    this.displayCategoryNames = [...this.defaultCategoryOrder];
    this.syncAdventureTypesFromVisibleCategories();
  }

  /**
   * Same resolution as header: location-specific config, then active config.
   */
  private loadHomeSiteConfiguration(): void {
    const location = this.locationContext.location;
    this.service.getSiteConfigurationByLocation(location).subscribe({
      next: (config) => {
        if (config) {
          this.applyHomeSiteConfig(config);
          return;
        }
        this.service.getActiveSiteConfiguration().subscribe({
          next: (fallback) => {
            if (fallback) {
              this.applyHomeSiteConfig(fallback);
            }
          },
          error: () => {}
        });
      },
      error: () => {
        this.service.getActiveSiteConfiguration().subscribe({
          next: (fallback) => {
            if (fallback) {
              this.applyHomeSiteConfig(fallback);
            }
          },
          error: () => {}
        });
      }
    });
  }

  private applyHomeSiteConfig(config: SiteConfigurationSchema): void {
    const metadata = this.safeParseSiteConfigurationMetadata(config.metadata);
    const mainHero = metadata.mainHero;
    const configuredHeadline = mainHero?.headline?.trim();

    if (configuredHeadline) {
      this.heroHeadline = configuredHeadline;
    } else if (config.siteName?.trim()) {
      this.heroHeadline = `Find Your Adventure in ${config.siteName.trim()}!`;
    } else {
      this.heroHeadline = 'Find Your Adventure!';
    }

    if (config.siteName?.trim()) {
      document.title = config.siteName.trim();
    }

    const desc = config.description?.trim();
    this.siteDescription = desc ? desc : null;

    this.sitePrimaryColor = config.primaryColor?.trim() || null;
    this.siteSecondaryColor = config.secondaryColor?.trim() || null;

    this.heroVideoUrl = this.resolveHeroVideoUrl(mainHero);

    if (this.heroVideoUrl) {
      // Optional poster / fallback URL from config only
      this.heroBannerUrl =
        mainHero?.imageUrl?.trim() || config.headerImageUrl?.trim() || '';
    } else if (mainHero?.imageUrl?.trim()) {
      this.heroBannerUrl = mainHero.imageUrl.trim();
    } else if (config.headerImageUrl?.trim()) {
      this.heroBannerUrl = config.headerImageUrl.trim();
    } else {
      this.heroBannerUrl = '';
    }

    const order = metadata.categoryOrder;
    this.displayCategoryNames =
      order?.length === this.defaultCategoryOrder.length ? [...order] : [...this.defaultCategoryOrder];

    const categoryImages = (metadata.categoryImages ?? {}) as Record<string, { imageUrl?: string }>;

    this.categoryImageUrls = {};
    for (const [categoryName, value] of Object.entries(categoryImages)) {
      if (value?.imageUrl?.trim()) {
        this.categoryImageUrls[categoryName] = value.imageUrl.trim();
      }
    }

    this.syncAdventureTypesFromVisibleCategories();
  }

  private resolveHeroVideoUrl(mainHero: SiteConfigurationMetadata['mainHero']): string | null {
    const key = mainHero?.videoKey?.trim();
    if (key) {
      try {
        return this.storageService.buildGuestPublicUrl(key);
      } catch {
        // Fall through to any persisted URL.
      }
    }

    const persisted = mainHero?.videoUrl?.trim();
    if (!persisted) {
      return null;
    }

    // Convert legacy presigned URLs to stable public URLs.
    if (persisted.includes('X-Amz-Signature=') || persisted.includes('?')) {
      try {
        const path = new URL(persisted).pathname.replace(/^\/+/, '');
        if (path.startsWith('public/')) {
          return this.storageService.buildGuestPublicUrl(path.slice('public/'.length));
        }
      } catch {
        return null;
      }
    }

    return persisted;
  }

  private syncAdventureTypesFromVisibleCategories(): void {
    this.adventureTypes = this.categoriesWithImages.map((name, idx) => ({
      id: `pic${idx + 1}`,
      name,
      url: this.getCategoryImageUrl(name)
    }));
  }

  private safeParseSiteConfigurationMetadata(
    metadata: string | undefined | null | Record<string, unknown>
  ): SiteConfigurationMetadata {
    if (metadata == null || metadata === '') return {};
    if (typeof metadata === 'object' && !Array.isArray(metadata)) {
      return metadata as SiteConfigurationMetadata;
    }
    try {
      return (JSON.parse(metadata as string) as SiteConfigurationMetadata) ?? {};
    } catch {
      return {};
    }
  }

}
